// @ts-nocheck
// ============================================
// History Slice - управление историей изменений (undo/redo)
// ============================================

import { StateCreator } from 'zustand';
import { App, HistoryRecord } from '../../types';

// Maximum number of undo states to keep
const MAX_UNDO_HISTORY = 50;

export interface AppStateSnapshot {
  apps: App[];
  timestamp: number;
  action: string;
  currentAppId: string | null;
  currentVersionId: string | null;
  currentScreenId: string | null;
}

export interface HistoryState {
  // History state
  undoStack: AppStateSnapshot[];
  redoStack: AppStateSnapshot[];
  lastAction: string | null;
  
  // History actions
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearUndoHistory: () => void;
  pushToHistory: (snapshot: Omit<AppStateSnapshot, 'timestamp'>) => void;
}

export const createHistorySlice: StateCreator<HistoryState, [], [], HistoryState> = (set, get) => ({
  // Initial state
  undoStack: [],
  redoStack: [],
  lastAction: null,
  
  // Undo
  undo: () => {
    const { undoStack, redoStack, apps } = get();
    if (undoStack.length === 0) return;
    
    const currentSnapshot: AppStateSnapshot = {
      apps: JSON.parse(JSON.stringify(apps)),
      timestamp: Date.now(),
      action: get().lastAction || 'Невідома дія',
      currentAppId: (get() as any).currentAppId,
      currentVersionId: (get() as any).currentVersionId,
      currentScreenId: (get() as any).currentScreenId
    };
    
    const previousSnapshot = undoStack[undoStack.length - 1];
    
    set({
      undoStack: undoStack.slice(0, -1),
      redoStack: [...redoStack, currentSnapshot],
      apps: previousSnapshot.apps,
      lastAction: previousSnapshot.action
    });
    
    const snapshotAppId = previousSnapshot.currentAppId;
    const snapshotVersionId = previousSnapshot.currentVersionId;
    const snapshotScreenId = previousSnapshot.currentScreenId;
    
    if (snapshotAppId) {
      const app = previousSnapshot.apps.find((a: App) => a.id === snapshotAppId);
      if (app) {
        const version = app.versions.find(v => v.id === snapshotVersionId) || 
                       app.versions.find(v => v.isActive) || 
                       app.versions[0];
        const screen = version?.screens.find(s => s.id === snapshotScreenId) || 
                      version?.screens[0];
        
        set({
          currentApp: app,
          currentAppId: app.id,
          currentVersion: version || null,
          currentVersionId: version?.id || null,
          currentScreen: screen || null,
          currentScreenId: screen?.id || null,
          selectedEvent: null
        });
      }
    } else {
      set({
        currentApp: null,
        currentAppId: null,
        currentVersion: null,
        currentVersionId: null,
        currentScreen: null,
        currentScreenId: null,
        selectedEvent: null
      });
    }
  },
  
  // Redo
  redo: () => {
    const { redoStack, undoStack, apps } = get();
    if (redoStack.length === 0) return;
    
    const currentSnapshot: AppStateSnapshot = {
      apps: JSON.parse(JSON.stringify(apps)),
      timestamp: Date.now(),
      action: get().lastAction || 'Невідома дія',
      currentAppId: (get() as any).currentAppId,
      currentVersionId: (get() as any).currentVersionId,
      currentScreenId: (get() as any).currentScreenId
    };
    
    const nextSnapshot = redoStack[redoStack.length - 1];
    
    set({
      redoStack: redoStack.slice(0, -1),
      undoStack: [...undoStack, currentSnapshot],
      apps: nextSnapshot.apps,
      lastAction: nextSnapshot.action
    });
    
    const snapshotAppId = nextSnapshot.currentAppId;
    const snapshotVersionId = nextSnapshot.currentVersionId;
    const snapshotScreenId = nextSnapshot.currentScreenId;
    
    if (snapshotAppId) {
      const app = nextSnapshot.apps.find((a: App) => a.id === snapshotAppId);
      if (app) {
        const version = app.versions.find(v => v.id === snapshotVersionId) || 
                       app.versions.find(v => v.isActive) || 
                       app.versions[0];
        const screen = version?.screens.find(s => s.id === snapshotScreenId) || 
                      version?.screens[0];
        
        set({
          currentApp: app,
          currentAppId: app.id,
          currentVersion: version || null,
          currentVersionId: version?.id || null,
          currentScreen: screen || null,
          currentScreenId: screen?.id || null,
          selectedEvent: null
        });
      }
    } else {
      set({
        currentApp: null,
        currentAppId: null,
        currentVersion: null,
        currentVersionId: null,
        currentScreen: null,
        currentScreenId: null,
        selectedEvent: null
      });
    }
  },
  
  // Can undo
  canUndo: () => get().undoStack.length > 0,
  
  // Can redo
  canRedo: () => get().redoStack.length > 0,
  
  // Clear undo history
  clearUndoHistory: () => {
    set({ undoStack: [], redoStack: [], lastAction: null });
  },
  
  // Push to history
  pushToHistory: (snapshot: Omit<AppStateSnapshot, 'timestamp'>) => {
    const { undoStack, apps, currentAppId, currentVersionId, currentScreenId } = get();
    
    const fullSnapshot: AppStateSnapshot = {
      ...snapshot,
      apps: JSON.parse(JSON.stringify(apps)),
      timestamp: Date.now(),
      currentAppId: currentAppId || snapshot.currentAppId,
      currentVersionId: currentVersionId || snapshot.currentVersionId,
      currentScreenId: currentScreenId || snapshot.currentScreenId
    };
    
    set({
      undoStack: [...undoStack, fullSnapshot].slice(-MAX_UNDO_HISTORY),
      redoStack: [],
      lastAction: snapshot.action
    });
  }
});
